import { useState } from "react";
import { MoreFieldRow, type MorePanelField } from "@/components/shared/MoreFieldsPanel";
import UserAvatar from "@/components/shared/Avatar";
import {
  User as UserIcon,
  Briefcase,
  Building2,
  Mail,
  Phone,
  MapPin,
  UserCircle2,
  Calendar,
  Clock,
  Video,
  Activity,
  Link as LinkIcon,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import LinkedInIcon from "@/components/shared/LinkedInIcon";

/**
 * Reference preview of a person drill-down card populated with one row for
 * every supported custom-field type. Used from the hidden UI tab so designers
 * can eyeball how each type renders on the card.
 */
export default function AllFieldTypesPreview() {
  const initial: Record<string, MorePanelField["value"]> = {
    // Standard fields (mirrors the People grid defaults)
    firstName: "Jamie",
    lastName: "Rivera",
    title: "Head of Growth",
    company: "Acme, Inc.",
    email: "jamie@acme.com",
    createdBy: "Alex Chen",
    createdDate: "2025-11-02",
    phone: "+14155551515",
    location: "San Francisco, CA",
    linkedin: "https://linkedin.com/in/jamierivera",
    lastEmail: "3 days ago",
    lastMeeting: "Nov 12, 2025",
    nextMeeting: "Dec 3, 2025",
    lastStep: "Sent pricing deck",
    nextStep: "Follow up on procurement review",
    source: "Inbound — website demo",
    // Sample custom fields (one per supported type)
    text: "Enterprise segment",
    number: 42,
    currency: "1500",
    date: "2026-03-15",
    checkbox: true,
    "single-select": "Warm",
    "multi-select": ["SaaS", "Fintech"],
    altEmail: "jamie.r@personal.com",
    hqLocation: "New York, NY",
    altPhone: "+14155550100",
    "traffic-light": "green",
    url: "https://acme.com",
    ai: "Yes — SOC 2 Type II compliant since 2024.",
    formula: "$2,100",
  };
  const [values, setValues] = useState(initial);
  const [moreOpen, setMoreOpen] = useState(false);

  const singleSelectOptions = ["Cold", "Warm", "Hot", "Customer"];
  const multiSelectOptions = ["SaaS", "Fintech", "AI", "Healthcare", "Retail"];

  const setValue = (key: string, v: string | boolean | string[]) =>
    setValues(prev => ({ ...prev, [key]: v }));

  // Above the fold: default-visible People columns (mirrors PeoplePage.tsx).
  const visibleFields: MorePanelField[] = [
    { key: "title", label: "Title", type: "text", value: values.title, icon: Briefcase },
    { key: "company", label: "Account", type: "text", value: values.company, icon: Building2, readOnly: true },
    { key: "email", label: "Email", type: "email", value: values.email, icon: Mail },
    { key: "createdBy", label: "Created By", type: "text", value: values.createdBy, icon: UserCircle2, readOnly: true },
    { key: "phone", label: "Phone", type: "phone", value: values.phone, icon: Phone },
    { key: "location", label: "Location", type: "location", value: values.location, icon: MapPin },
    { key: "linkedin", label: "LinkedIn", type: "url", value: values.linkedin, icon: LinkedInIcon },
    { key: "lastEmail", label: "Last Email", type: "text", value: values.lastEmail, icon: Clock, readOnly: true },
    { key: "lastMeeting", label: "Last Meeting", type: "text", value: values.lastMeeting, icon: Calendar, readOnly: true },
    { key: "nextMeeting", label: "Next Meeting", type: "text", value: values.nextMeeting, icon: Calendar, readOnly: true },
    { key: "lastStep", label: "Last Step", type: "text", value: values.lastStep, icon: Activity },
    { key: "nextStep", label: "Next Step", type: "text", value: values.nextStep, icon: Activity },
  ];

  // More fields: default-hidden People columns + one sample per custom field type.
  const hiddenStandardFields: MorePanelField[] = [
    { key: "firstName", label: "First Name", type: "text", value: values.firstName, icon: UserIcon },
    { key: "lastName", label: "Last Name", type: "text", value: values.lastName, icon: UserIcon },
    { key: "source", label: "Source", type: "text", value: values.source, icon: LinkIcon, readOnly: true },
    { key: "createdDate", label: "Create Date", type: "date", value: values.createdDate, icon: Calendar, readOnly: true },
  ];

  const customSampleFields: MorePanelField[] = [
    { key: "text", label: "Segment", type: "text", value: values.text },
    { key: "number", label: "Employees", type: "number", value: values.number },
    { key: "currency", label: "Deal size", type: "currency", value: values.currency },
    { key: "date", label: "Renewal date", type: "date", value: values.date },
    { key: "checkbox", label: "Newsletter", type: "checkbox", value: values.checkbox },
    { key: "single-select", label: "Lead temp", type: "single-select", value: values["single-select"], options: singleSelectOptions },
    { key: "multi-select", label: "Industries", type: "multi-select", value: values["multi-select"], options: multiSelectOptions },
    { key: "altEmail", label: "Alt email", type: "email", value: values.altEmail },
    { key: "hqLocation", label: "HQ location", type: "location", value: values.hqLocation },
    { key: "altPhone", label: "Alt phone", type: "phone", value: values.altPhone },
    { key: "traffic-light", label: "Health", type: "traffic-light", value: values["traffic-light"] },
    { key: "url", label: "Website", type: "url", value: values.url },
    { key: "ai", label: "SOC 2 compliant?", type: "ai", value: values.ai, readOnly: true },
    { key: "formula", label: "ARR estimate", type: "formula", value: values.formula, readOnly: true },
  ];

  const moreFields = [...hiddenStandardFields, ...customSampleFields];

  return (
    <div className="rounded-lg border border-border bg-card/50 overflow-hidden max-w-xl">
      {/* Faux header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <UserAvatar name={`${values.firstName} ${values.lastName}`} size="md" />
        <div className="min-w-0">
          <div className="text-base font-semibold truncate">
            {String(values.firstName)} {String(values.lastName)}
          </div>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="space-y-0.5">
          {visibleFields.map(f => (
            <MoreFieldRow key={f.key} field={f} onChange={setValue} />
          ))}
        </div>

        <button
          type="button"
          onClick={() => setMoreOpen(o => !o)}
          className="mt-2 flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
        >
          {moreOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
          <span>{moreOpen ? "Less fields" : "More fields"}</span>
        </button>

        {moreOpen && (
          <div className="mt-2 space-y-0.5">
            {moreFields.map(f => (
              <MoreFieldRow key={f.key} field={f} onChange={setValue} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}